// React
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react';

// Refine
import { useDocumentTitle } from '@refinedev/react-router-v6';
import { useNotification } from '@refinedev/core';
import { useTranslation } from '@refinedev/core';

// Mantine
import {
  LoadingOverlay,
  Text,
  useComputedColorScheme,
  useMantineTheme,
} from '@mantine/core';
import {
  MRT_ColumnFiltersState,
  MRT_Row,
  MRT_SortingState,
  MRT_TableInstance,
  MRT_TableOptions,
} from 'mantine-react-table';

// Resource
import { generateReportV2 } from 'api/action';
import { useListUsersByOrganization } from 'api/resource';
import {
  AccountsReceivableReportHeaders,
  TariffProviderReportHeaders,
} from 'constants/reportHeaderConstants';

// Components
import { BillingDataGridWrapper } from 'components/common/BillingDataGridWrapper';

import { ReportModal, ReportTopToolbar } from './components';
import { Center, Stack } from '@mantine/core';
import { IconReportAnalytics } from '@tabler/icons-react';

// Interface
import {
  FormReport,
  Payor,
  Provider,
  Remittance,
  Report,
  ReportType,
  Site,
  Submission,
  User,
} from 'interfaces';

// Columns
import { ReportColumns } from 'columns/Report';

// Icons
import { TableAlertBanner } from 'components/common/TableAlertBanner';

// Hooks
import { useDisableReportChip, useIsProvider } from 'hooks';
import { useCustomColumnSorting } from 'hooks/useCustomColumnSorting';
import { useCustomColumnVisibility } from 'hooks/useCustomColumnVisibility';
import { useCurrentUserStore } from 'store/UserStore';
import { useCurrentSiteStore } from 'store/SiteStore';
import { ReportRowActions } from './components/ReportRowActions';
import type { ReportGenerationRequest } from '@billing/validation/report';

interface ReportManagementProps {
  reports: Report[];
  sites: Site[];
  providers: Provider[];
  submissions: Submission[];
  remittances: Remittance[];
  payors: Payor[];
  totalReports?: number;
  pagination?: {
    pageIndex: number;
    pageSize: number;
    globalFilter: string;
    columnFilters: MRT_ColumnFiltersState;
    sorting: MRT_SortingState;
  };
  setPagination?: Dispatch<
    SetStateAction<{
      pageIndex: number;
      pageSize: number;
      globalFilter: string;
      columnFilters: MRT_ColumnFiltersState;
      sorting: MRT_SortingState;
    }>
  >;
  isLoading: boolean;
  isError: boolean;
  isFetching: boolean;
  refetchReports: () => void;
  errorMessage: string;
}

interface BuildReportRequestOptions {
  values: FormReport;
  siteID: string;
  userID: string;
  cognitoID: string;
}

const buildReportRequest = ({
  values,
  siteID,
  userID,
  cognitoID,
}: BuildReportRequestOptions): ReportGenerationRequest => {
  const exportType =
    values.exportType === 'csv' || values.exportType === 'pdf'
      ? values.exportType
      : 'pdf';
  const monthlyRevenueStatementScope =
    values.reportScope === 'site' ? 'site' : 'provider';

  switch (values.subtype) {
    case 'accountsReceivable':
      return {
        type: ReportType.accountsReceivable,
        payload: {
          siteID,
          userID,
          cognitoID,
          startDate: values.startDate,
          endDate: values.endDate,
          headers: AccountsReceivableReportHeaders,
          providerID: values.providerID,
          exportType,
        },
      };
    case 'grouping':
      if (values.reportScope === 'group') {
        return {
          type: ReportType.groupIncome,
          payload: {
            siteID,
            userID,
            groupName: values.groupName,
            remittanceID: values.remittanceID,
            exportType,
          },
        };
      }
      break;
    case 'statementIncome':
      return {
        type: ReportType.statementIncome,
        payload: {
          siteID,
          cognitoID,
          providerID: values.providerID,
          remittanceID: values.remittanceID,
        },
      };
    case 'tariffProviderReport':
      return {
        type: ReportType.tariffProviderReport,
        payload: {
          siteID,
          userID,
          cognitoID,
          providerID: values.providerID,
          startDate: values.startDate,
          endDate: values.endDate,
          headers: TariffProviderReportHeaders,
          tariffIDs: values.tariffIDs,
        },
      };
  }

  return {
    type: ReportType.providerReport,
    payload: {
      siteID,
      userID,
      providerID: values.providerID,
      subtype: values.subtype,
      startDate: values.startDate,
      endDate: values.endDate,
      remittanceID: values.remittanceID,
      dateType: values.dateType,
      groupIDs: values.groupIDs,
      reportScope:
        values.subtype === 'monthlyRevenueStatement'
          ? monthlyRevenueStatementScope
          : undefined,
      exportType,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    },
  };
};

export const ReportManagement: React.FC<ReportManagementProps> = ({
  reports,
  sites,
  providers,
  remittances,
  submissions,
  payors,
  totalReports,
  pagination,
  setPagination,
  isLoading,
  isError,
  isFetching,
  errorMessage,
}) => {
  const { translate } = useTranslation();
  const title = translate('pages:reports.pageTitle');
  useDocumentTitle(translate('pages:reports.documentTitle'));
  const theme = useMantineTheme();
  const computedColorScheme = useComputedColorScheme('dark');
  const { open } = useNotification();

  const isProvider = useIsProvider();
  const [pdfLoading, setPdfLoading] = useState<boolean>(false);
  const { id: userID, cognitoID } = useCurrentUserStore();
  const {
    id: siteID,
    organizationID,
    providerSiteSelection,
  } = useCurrentSiteStore();
  const isAllSites = providerSiteSelection === 'all';
  const { data: organizationUsers, isSuccess } = useListUsersByOrganization({
    orgID: isProvider ? '' : organizationID,
  });

  const usersByCognitoId = useMemo<Record<string, User>>(() => {
    if (!isSuccess || !organizationUsers) {
      return {};
    }

    return organizationUsers.reduce((acc, user) => {
      if (user?.cognitoID) {
        acc[user.cognitoID] = user;
      }
      return acc;
    }, {} as Record<string, User>);
  }, [isSuccess, organizationUsers]);

  const disableChip = useDisableReportChip({
    submissions,
    sites,
    providers,
  });
  const setPageIndex = (pageIndex: number) => {
    if (!setPagination) return;
    setPagination((prev) => ({ ...prev, pageIndex }));
  };
  const setGlobalFilter = (globalFilter: string) => {
    if (!setPagination) return;
    setPagination((prev) => ({ ...prev, globalFilter }));
  };
  const setColumnFilters = (columnFilters: MRT_ColumnFiltersState) => {
    if (!setPagination) return;
    setPagination((prev) => ({ ...prev, columnFilters }));
  };
  const handleCreateReport = async ({
    values,
    table,
  }: {
    values: FormReport;
    table: MRT_TableInstance<Report>;
  }) => {
    try {
      values.siteID = siteID;
      values.userID = userID;
      setPdfLoading(true);
      const { type: pdfType, payload } = buildReportRequest({
        values,
        siteID,
        userID,
        cognitoID,
      });

      await generateReportV2(ReportType[pdfType], payload);
      open?.({
        message: translate('pages:reports.generateReport.inProgress'),
        type: 'success',
        key: 'success',
      });
    } catch (error) {
      open?.({
        message: translate('pages:reports.generateReport.error'),
        type: 'error',
        key: 'error',
      });
    } finally {
      setPdfLoading(false);
      table.setCreatingRow(null);
      table.setEditingRow(null);
    }
  };

  const columns = ReportColumns({
    sites,
    providers,
    remittances,
    submissions,
    payors,
    usersByCognitoId,
  });
  const initialColumnVisibility = {
    headers: false,
    siteID: false,
    startDate: false,
    tariffID: false,
    patientID: false,
    submissionID: false,
    payorID: false,
    userID: false,
  };
  const { columnVisibility, onColumnVisibilityChange } =
    useCustomColumnVisibility({
      initialColumns: initialColumnVisibility,
      conditionalColumns: { siteID: isProvider && isAllSites },
    });
  const handleSortingChange = useCallback(
    (sorting: MRT_SortingState) => {
      if (!setPagination) return;
      setPagination((prev) => ({ ...prev, sorting, pageIndex: 0 }));
    },
    [setPagination]
  );
  const { sorting, onSortingChange } = useCustomColumnSorting({
    initialSorting: pagination?.sorting,
    onSortingChange: handleSortingChange,
  });

  const tableOptions: MRT_TableOptions<Report> = {
    data: reports,
    columns,
    enableFullScreenToggle: false,
    enableGrouping: true,
    enableColumnDragging: false,
    enableSorting: true,
    paginateExpandedRows: false,
    enableFilters: true,
    enableGlobalFilter: true,
    initialState: {
      ...(pagination
        ? {
            globalFilter: pagination.globalFilter,
            columnFilters: pagination.columnFilters,
            showGlobalFilter: !!pagination.globalFilter,
          }
        : {}),
    },
    state: {
      isLoading: isLoading,
      showAlertBanner: isError,
      showProgressBars: isFetching || isLoading,
      ...(pagination
        ? {
            pagination,
            globalFilter: pagination.globalFilter,
            columnFilters: pagination.columnFilters,
          }
        : {}),
      sorting,
      columnVisibility,
    },

    renderEmptyRowsFallback: () => {
      return (
        <Center style={{ height: '100%', margin: '7%' }}>
          <Stack align="center">
            <IconReportAnalytics
              size={64}
              color={`${theme.colors[`primary-${computedColorScheme}`][0]}`}
            />
            <Text
              size="lg"
              c={`${theme.colors[`gray-${computedColorScheme}`][0]}`}
            >
              {translate('pages:reports.noReportsFound')}
            </Text>
          </Stack>
        </Center>
      );
    },
    mantineToolbarAlertBannerProps: () => {
      return TableAlertBanner({
        isError: isError,
        message: errorMessage,
      });
    },
    mantineLoadingOverlayProps: { visible: isLoading || isFetching },
    createDisplayMode: 'modal',
    editDisplayMode: 'modal',
    enableEditing: true,
    onSortingChange,
    onColumnVisibilityChange,
    ...(setPagination
      ? {
          onColumnFiltersChange: (
            updater:
              | MRT_ColumnFiltersState
              | ((old: MRT_ColumnFiltersState) => MRT_ColumnFiltersState)
          ) => {
            const nextFilters =
              typeof updater === 'function'
                ? updater(pagination?.columnFilters || [])
                : updater;
            setColumnFilters(nextFilters);
            setPageIndex(0);
          },
          onGlobalFilterChange: (
            updater: string | ((old: string) => string)
          ) => {
            const nextGlobalFilter =
              typeof updater === 'function'
                ? updater(pagination?.globalFilter || '')
                : updater;
            setGlobalFilter(nextGlobalFilter);
            setPageIndex(0);
          },
        }
      : {}),
    ...(setPagination ? { onPaginationChange: setPagination } : {}),
    onCreatingRowSave: handleCreateReport,
    mantineSearchTextInputProps: {
      placeholder: translate('pages:reports.searchReports'),
    },
    renderCreateRowModalContent: ({ table, row, internalEditComponents }) => (
      <>
        <LoadingOverlay visible={pdfLoading} />
        <ReportModal
          title="Create Report"
          row={row}
          table={table}
          providers={providers}
          remittances={remittances}
          internalEditComponents={internalEditComponents}
          setIsLoading={setPdfLoading}
          disableChip={disableChip}
        />
      </>
    ),
    mantineCreateRowModalProps: {
      withCloseButton: true,
      closeOnClickOutside: false,
      closeOnEscape: false,
      size: 'xl',
      centered: true,
      withOverlay: true,
      overlayProps: {
        backgroundOpacity: 0.5,
        blur: 5,
      },
      styles: {
        header: {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          alignContent: 'center',
        },
        title: {
          fontSize: '1.875rem',
          textAlign: 'center',
          display: 'flex',
          width: '100%',
          justifyContent: 'center',
        },
      },
    },
    renderEditRowModalContent: ({ table, row, internalEditComponents }) => (
      <>
        <LoadingOverlay visible={pdfLoading} />
        <ReportModal
          title="Edit Report"
          row={row}
          table={table}
          providers={providers}
          remittances={remittances}
          internalEditComponents={internalEditComponents}
          setIsLoading={setPdfLoading}
        />
      </>
    ),
    renderTopToolbarCustomActions: ({ table }) => (
      <ReportTopToolbar
        table={table}
        isLoading={isLoading || isFetching}
        computedColorScheme={computedColorScheme}
        setIsLoading={setPdfLoading}
        providers={providers}
        remittances={remittances}
      />
    ),
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: 'Actions',
        visibleInShowHideMenu: false,
        size: 12,
        minSize: 12,
        maxSize: 12,
        Cell: ({ row }: { row: MRT_Row<Report> }) => (
          <ReportRowActions row={row} />
        ),
      },
    },
    ...(pagination
      ? {
          manualFiltering: true,
          manualSorting: true,
          manualPagination: true,
          rowCount: totalReports ?? reports.length,
        }
      : {}),
  };

  return (
    <BillingDataGridWrapper
      title={title}
      tableOptions={tableOptions}
      dataTestID="reportManagement"
      showAlertBanner={false}
    />
  );
};
